import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TableLibrary } from 'src/components/complex';
import TextareaAutosize from 'react-textarea-autosize';
import {
  convertMobileStyle,
  scssVariables as variables,
  remFontCss as mainRemFontCss,
  remCss
} from 'src/helpers';
import { MCText, View } from '../basic';
import LANG_CONST from '../../helpers/lang/const';

export const MCEditText = ({
  primary,
  heading,
  text,
  textLight,
  error,
  placeholder2,
  secondary,
  quaternary,
  white,
  weight = 400,
  size = 18,
  lineHeight,
  flex,
  style = {},
  remFontCss = mainRemFontCss,
  isTextArea = false,
  onEnter,
  onMove,
  onChange,
  value,
  ...props
}) => {
  const [alert, setAlert] = useState(null);
  const timeoutRef = useRef(null);

  const handleAlert = useCallback((nextAlert) => {
    setAlert(nextAlert);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (nextAlert)
      timeoutRef.current = setTimeout(() => {
        setAlert(null);
      }, 5000);
  }, []);

  const checkMaxLength = useCallback(
    (nextValue) => {
      handleAlert(null);
      if (props.maxLength && nextValue.length === props.maxLength) {
        handleAlert(LANG_CONST.notice.limit_reached);
      }
    },
    [handleAlert, props.maxLength]
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      checkMaxLength(value);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [checkMaxLength, value]);

  // default styles: medium, heading
  let attributes = {
    primary,
    heading,
    text,
    'text-light': textLight,
    error,
    secondary,
    quaternary,
    placeholder2,
    white
  };
  let color = Object.keys(attributes).find((k) => !!attributes[k]) || 'heading';

  let family = weight > 600 ? 'bold' : weight > 400 ? 'medium' : 'regular';

  let styles = {
    outline: 'none',
    border: 'none',
    color: variables['colors-mc-' + color],
    fontFamily: variables['fonts-mc-' + family],
    fontWeight: weight,
    fontSize: remFontCss(size),
    lineHeight: remFontCss(lineHeight || size + 6) + 'px',
    maxWidth: '100%',
    overflow: 'hidden',
    resize: 'none',
    ...convertMobileStyle(style)
  };

  return (
    <View style={{ flex: flex ? 1 : 0, gap: remCss(10) }}>
      <TextareaAutosize
        autoComplete={'off'}
        onKeyUp={(e) => {
          if (!isTextArea)
            if (e.key === 'Enter' || e.code === 'Enter') {
              e.preventDefault();
              onEnter(e);
              return false;
            }
        }}
        onKeyPress={(e) => {
          checkMaxLength(e.target.value);
        }}
        onPaste={(e) => {
          checkMaxLength(e.target.value);
        }}
        onKeyDown={(e) => {
          if (
            (e.key === 'ArrowUp' && e.target.selectionStart === 0) ||
            (e.key === 'ArrowDown' && e.target.selectionStart === e.target.value.length) ||
            (e.key === 'ArrowLeft' && e.target.selectionStart === 0) ||
            (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length)
          ) {
            e.preventDefault();
            onMove(e, e.key.replace('Arrow', '').toLowerCase());
          }
        }}
        style={styles}
        onChange={(e) => {
          e.preventDefault();
          if (isTextArea) onChange(e, e.target.value);
          else onChange(e, e.target.value.replace(/[\n\r]+/, ''));
        }}
        value={value}
        {...props}
      />
      {alert && <MCText label={alert} primary />}
    </View>
  );
};

const MCBaseEditableTable = ({ list, columns, topRowExtend, ...props }) => {
  const editList = useRef([]);

  const onMove = (e, rowIndex, colIndex, move) => {
    let isNext = true;
    switch (move) {
      case 'up':
        rowIndex--;
        break;
      case 'down':
        rowIndex++;
        break;
      case 'left':
        colIndex--;
        isNext = false;
        break;
      case 'right':
        colIndex++;
        break;
    }
    handleMove(rowIndex, colIndex, isNext);
  };

  const handleMove = (rowIndex, colIndex, isNext) => {
    if (rowIndex < 0 || rowIndex > editList.current.length) return;
    const el = editList.current[rowIndex] && editList.current[rowIndex][colIndex];
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(el.value?.length, el.value?.length);
      }, 100);
    } else if (isNext) handleMove(rowIndex + 1, 0);
    else handleMove(rowIndex - 1, editList.current[rowIndex]?.length - 1);
  };

  const editPropsFn = (rowIndex, colIndex) => {
    return {
      ref: (ref) => {
        if (!editList.current[rowIndex]) {
          editList.current[rowIndex] = [];
        }
        editList.current[rowIndex][colIndex] = ref;
      },
      onEnter: (e) => onMove(e, rowIndex, colIndex, 'down'),
      onMove: (e, move) => onMove(e, rowIndex, colIndex, move)
    };
  };

  const convertColumns = (columns) => {
    return columns.map(({ renderEditCell, ...col }) => {
      return {
        ...col,
        renderCell: (item, rowIndex, ...a) => {
          return renderEditCell
            ? renderEditCell(editPropsFn, item, rowIndex, ...a)
            : col.renderCell(item, rowIndex, ...a);
        }
      };
    });
  };

  return (
    <TableLibrary
      template={1}
      columns={convertColumns(columns)}
      data={list}
      topRowExtend={() => {
        return topRowExtend && topRowExtend(editPropsFn);
      }}
      {...props}
    />
  );
};


MCBaseEditableTable.displayName = 'MCBaseEditableTable';

export default MCBaseEditableTable;
